/**
 * Instrument Icon Component
 * Displays appropriate icons for different instrument types:
 * - Forex: Country flags
 * - Crypto: Cryptocurrency logos
 * - Commodities: Resource icons
 * - Stocks: Company logos
 * - Indices: Market icons
 */
import React, { memo, useState } from 'react';

// Country flag codes for forex pairs
const CURRENCY_FLAGS = {
  'EUR': '🇪🇺', 'USD': '🇺🇸', 'GBP': '🇬🇧', 'JPY': '🇯🇵',
  'CHF': '🇨🇭', 'AUD': '🇦🇺', 'CAD': '🇨🇦', 'NZD': '🇳🇿',
  'TRY': '🇹🇷', 'ZAR': '🇿🇦', 'MXN': '🇲🇽', 'SGD': '🇸🇬',
  'HKD': '🇭🇰', 'NOK': '🇳🇴', 'SEK': '🇸🇪', 'DKK': '🇩🇰',
  'PLN': '🇵🇱', 'CNY': '🇨🇳', 'CNH': '🇨🇳', 'INR': '🇮🇳',
  'RUB': '🇷🇺', 'BRL': '🇧🇷', 'KRW': '🇰🇷', 'THB': '🇹🇭',
};

// Crypto logo URLs from CoinGecko/CryptoIcons CDN
const CRYPTO_ICONS = {
  'BTC': 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
  'ETH': 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
  'XRP': 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png',
  'SOL': 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
  'BNB': 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
  'ADA': 'https://assets.coingecko.com/coins/images/975/small/cardano.png',
  'DOGE': 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
  'DOT': 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png',
  'AVAX': 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
  'MATIC': 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png',
  'LINK': 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',
  'LTC': 'https://assets.coingecko.com/coins/images/2/small/litecoin.png',
  'UNI': 'https://assets.coingecko.com/coins/images/12504/small/uniswap-uni.png',
  'ATOM': 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png',
};

// Commodity icons - Using emoji fallbacks to avoid CORS issues
const COMMODITY_ICONS = {
  'XAU': { 
    image: null,
    fallback: '🥇', 
    color: '#FFD700', 
    label: 'Gold' 
  },
  'XAG': { 
    image: null,
    fallback: '🥈', 
    color: '#C0C0C0', 
    label: 'Silver' 
  },
  'XPT': { 
    image: null,
    fallback: '⬜', 
    color: '#E5E4E2', 
    label: 'Platinum' 
  },
  'XPD': { 
    image: null,
    fallback: '⬜', 
    color: '#CED0DD', 
    label: 'Palladium' 
  },
  'COPPER': { 
    image: null,
    fallback: '🟤', 
    color: '#B87333', 
    label: 'Copper' 
  },
  'OIL': { 
    image: null,
    fallback: '🛢️', 
    color: '#1a1a1a', 
    label: 'Oil' 
  },
  'USOIL': { 
    image: null,
    fallback: '🛢️', 
    color: '#1a1a1a', 
    label: 'WTI Oil' 
  },
  'UKOIL': { 
    image: null,
    fallback: '🛢️', 
    color: '#2d3748', 
    label: 'Brent' 
  },
  'NATGAS': { 
    image: null,
    fallback: '🔥', 
    color: '#3182ce', 
    label: 'Natural Gas' 
  },
  'WHEAT': { 
    image: null,
    fallback: '🌾', 
    color: '#D4A017', 
    label: 'Wheat' 
  },
  'CORN': { 
    image: null,
    fallback: '🌽', 
    color: '#FFD700', 
    label: 'Corn' 
  },
  'SOYBEAN': { 
    image: null,
    fallback: '🫘', 
    color: '#8B4513', 
    label: 'Soybean' 
  },
  'COFFEE': { 
    image: null,
    fallback: '☕', 
    color: '#6F4E37', 
    label: 'Coffee' 
  },
  'SUGAR': { 
    image: null,
    fallback: '🍬', 
    color: '#FFFFFF', 
    label: 'Sugar' 
  },
  'COTTON': { 
    image: null,
    fallback: '☁️', 
    color: '#F5F5DC', 
    label: 'Cotton' 
  },
};

// Stock company logos - Using emoji fallbacks to avoid CORS issues in preview
const STOCK_LOGOS = {
  // Note: External URLs blocked in preview environment - using fallbacks
  'AAPL': null,
  'MSFT': null,
  'GOOGL': null,
  'AMZN': null,
  'META': null,
  'TSLA': null,
  'NVDA': null,
  'AMD': null,
  'INTC': null,
  'NFLX': null,
  // BIST stocks - Turkish companies
  'THYAO': null,
  'GARAN': null,
  'AKBNK': null,
  'TCELL': null,
  // All BIST stocks use null (emoji fallback) to avoid CORS in preview
  'TUPRS': null,
  'EREGL': null,
  'KCHOL': null,
  'SAHOL': null,
  'ASELS': null,
  'SISE': null,
  'BIMAS': null,
  'FROTO': null,
  'ARCLK': null,
  'VESTL': null,
  'TOASO': null,
  'PETKM': null,
  'HALKB': null,
  'VAKBN': null,
  'ISCTR': null,
  'YKBNK': null,
  'PGSUS': null,
  'TTKOM': null,
  'MGROS': null,
  'ENKAI': null,
  'KOZAL': null,
};

// Index icons with country/region flags
const INDEX_ICONS = {
  'US100': { flag: '🇺🇸', name: 'NASDAQ', color: '#00AAFF' },
  'US500': { flag: '🇺🇸', name: 'S&P', color: '#E60000' },
  'US30': { flag: '🇺🇸', name: 'DOW', color: '#003087' },
  'GER40': { flag: '🇩🇪', name: 'DAX', color: '#FFCC00' },
  'UK100': { flag: '🇬🇧', name: 'FTSE', color: '#CF142B' },
  'FRA40': { flag: '🇫🇷', name: 'CAC', color: '#0055A4' },
  'ESP35': { flag: '🇪🇸', name: 'IBEX', color: '#AA151B' },
  'EU50': { flag: '🇪🇺', name: 'STOXX', color: '#003399' },
  'JP225': { flag: '🇯🇵', name: 'NIKKEI', color: '#BC002D' },
  'HK50': { flag: '🇭🇰', name: 'HSI', color: '#DE2910' },
  'AU200': { flag: '🇦🇺', name: 'ASX', color: '#00008B' },
  'CN50': { flag: '🇨🇳', name: 'A50', color: '#DE2910' },
  'XU100': { flag: '🇹🇷', name: 'BIST', color: '#E30A17' },
  'XU030': { flag: '🇹🇷', name: 'BIST30', color: '#E30A17' },
};

// Parse forex pair to get currencies
const parseForexPair = (symbol) => {
  const clean = symbol.replace('/', '').toUpperCase();
  if (clean.length === 6) {
    return {
      base: clean.substring(0, 3),
      quote: clean.substring(3, 6)
    };
  }
  return null;
};

// Get crypto symbol from trading pair
const getCryptoSymbol = (symbol) => {
  const clean = symbol.replace('/', '').replace('USD', '').replace('USDT', '').toUpperCase();
  return CRYPTO_ICONS[clean] ? clean : null;
};

// Forex Icon with two flags
const ForexIcon = memo(({ symbol, size = 24 }) => {
  const pair = parseForexPair(symbol);
  if (!pair) return null;
  
  const baseFlag = CURRENCY_FLAGS[pair.base] || '🏳️';
  const quoteFlag = CURRENCY_FLAGS[pair.quote] || '🏳️';
  
  return (
    <div className="flex items-center" style={{ width: size + 8, height: size }}>
      <span style={{ fontSize: size * 0.7 }}>{baseFlag}</span>
      <span style={{ fontSize: size * 0.7, marginLeft: -4 }}>{quoteFlag}</span>
    </div>
  );
});

// Crypto Icon
const CryptoIcon = memo(({ symbol, size = 24, isDark }) => {
  const cryptoSymbol = getCryptoSymbol(symbol);
  const iconUrl = cryptoSymbol ? CRYPTO_ICONS[cryptoSymbol] : null;
  
  if (iconUrl) {
    return (
      <img 
        src={iconUrl} 
        alt={cryptoSymbol}
        style={{ width: size, height: size }}
        className="rounded-full"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Fallback crypto icon
  return (
    <div 
      className={`flex items-center justify-center rounded-full ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}
      style={{ width: size, height: size }}
    >
      <span className="text-orange-500 font-bold" style={{ fontSize: size * 0.5 }}>₿</span>
    </div>
  );
});

// Commodity Icon with image support
const CommodityIcon = memo(({ symbol, size = 24, isDark }) => {
  const [imageError, setImageError] = useState(false);
  const clean = symbol.replace('/', '').replace('USD', '').toUpperCase();
  const commodity = COMMODITY_ICONS[clean] || COMMODITY_ICONS[symbol.toUpperCase()];
  
  if (commodity && commodity.image && !imageError) {
    return (
      <div 
        className="flex items-center justify-center rounded-full overflow-hidden"
        style={{ 
          width: size, 
          height: size,
          backgroundColor: `${commodity.color}20`,
        }}
      >
        <img 
          src={commodity.image}
          alt={commodity.label}
          style={{ width: size - 4, height: size - 4 }}
          className="object-contain"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }
  
  if (commodity) {
    return (
      <div 
        className="flex items-center justify-center rounded-full"
        style={{ 
          width: size, 
          height: size,
          backgroundColor: `${commodity.color}20`,
        }}
      >
        <span style={{ fontSize: size * 0.65 }}>{commodity.fallback || commodity.emoji}</span>
      </div>
    );
  }
  
  // Generic commodity icon
  return (
    <div 
      className={`flex items-center justify-center rounded-full ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}
      style={{ width: size, height: size }}
    >
      <span style={{ fontSize: size * 0.6 }}>📦</span>
    </div>
  );
});

const StockIcon = memo(({ symbol, size = 24, isDark, isBIST = false, isEU = false }) => {
  const [imageError, setImageError] = useState(false);
  const clean = symbol.replace('.IS', '').toUpperCase();
  const logoUrl = STOCK_LOGOS[clean];
  
  // Check if this is a BIST stock
  const isBISTStock = isBIST || symbol.includes('.IS') || symbol.endsWith('.IS');

  // Check if this is an EU stock
  const isEUStock = isEU || symbol.endsWith('.DE') || symbol.endsWith('.PA') || symbol.endsWith('.SW') || symbol.endsWith('.AS') || symbol.endsWith('.L') || symbol.endsWith('.MC') || symbol.endsWith('.MI') || ['ASML', 'SAP'].includes(clean);
  
  if (logoUrl && !imageError) {
    return (
      <div 
        className="flex items-center justify-center rounded-full overflow-hidden bg-white"
        style={{ width: size, height: size }}
      >
        <img 
          src={logoUrl} 
          alt={clean}
          style={{ width: size - 2, height: size - 2 }}
          className="object-contain"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }
  
  // BIST stocks - show Turkish flag
  if (isBISTStock) {
    return (
      <div 
        className="flex items-center justify-center rounded-full"
        style={{ 
          width: size, 
          height: size,
          backgroundColor: '#E30A1720', // Turkish red with transparency
        }}
      >
        <span style={{ fontSize: size * 0.7 }}>🇹🇷</span>
      </div>
    );
  }

  // EU stocks - show country flag or European flag
  if (isEUStock) {
    let flag = '🇪🇺';
    if (symbol.endsWith('.DE') || clean === 'SAP') flag = '🇩🇪';
    else if (symbol.endsWith('.PA')) flag = '🇫🇷';
    else if (symbol.endsWith('.SW')) flag = '🇨🇭';
    else if (symbol.endsWith('.AS') || clean === 'ASML') flag = '🇳🇱';
    else if (symbol.endsWith('.L')) flag = '🇬🇧';
    else if (symbol.endsWith('.MC')) flag = '🇪🇸';
    else if (symbol.endsWith('.MI')) flag = '🇮🇹';
    else if (symbol.endsWith('.CO')) flag = '🇩🇰';
    
    return (
      <div 
        className="flex items-center justify-center rounded-full"
        style={{ 
          width: size, 
          height: size,
          backgroundColor: '#00339920', // European blue with transparency
        }}
      >
        <span style={{ fontSize: size * 0.7 }}>{flag}</span>
      </div>
    );
  }
  
  // Fallback stock icon with first letter (for non-BIST/non-EU stocks)
  return (
    <div 
      className={`flex items-center justify-center rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}
      style={{ width: size, height: size }}
    >
      <span className={`font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`} style={{ fontSize: size * 0.5 }}>
        {clean.charAt(0)}
      </span>
    </div>
  );
});

// Index Icon
const IndexIcon = memo(({ symbol, size = 24, isDark }) => {
  const clean = symbol.toUpperCase();
  const index = INDEX_ICONS[clean];
  
  if (index) {
    return (
      <div 
        className="flex items-center justify-center rounded-full relative overflow-hidden"
        style={{ 
          width: size, 
          height: size,
          backgroundColor: `${index.color}20`,
        }}
      >
        <span style={{ fontSize: size * 0.7 }}>{index.flag}</span>
      </div>
    );
  }
  
  // Generic index icon
  return (
    <div 
      className={`flex items-center justify-center rounded-full ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}
      style={{ width: size, height: size }}
    >
      <span style={{ fontSize: size * 0.6 }}>📈</span>
    </div>
  );
});

// Main Instrument Icon Component
const InstrumentIcon = ({ symbol, category, type, size = 24, isDark = true, tradingview_symbol }) => {
  const normalizedCategory = (category || type || '').toLowerCase();
  const cleanSymbol = symbol?.toUpperCase() || '';
  
  // COMMODITIES CHECK FIRST - oil, gold, silver, etc.
  // Check this BEFORE stocks to prevent oil being treated as stocks
  if (normalizedCategory === 'commodities' || 
      cleanSymbol.startsWith('XAU') || cleanSymbol.startsWith('XAG') ||
      cleanSymbol.startsWith('XPT') || cleanSymbol.startsWith('XPD') ||
      cleanSymbol.includes('OIL') || cleanSymbol === 'NATGAS' ||
      cleanSymbol === 'USOIL' || cleanSymbol === 'UKOIL' ||
      COMMODITY_ICONS[cleanSymbol] || COMMODITY_ICONS[cleanSymbol.replace('USD', '')]) {
    return <CommodityIcon symbol={symbol} size={size} isDark={isDark} />;
  }
  
  // Check if this is a BIST stock (must have .IS suffix or BIST: prefix or bist category)
  const isBIST = normalizedCategory === 'bist' || 
                 cleanSymbol.endsWith('.IS') || 
                 tradingview_symbol?.startsWith('BIST:');
  
  // Detect instrument type - ORDER MATTERS!
  if (normalizedCategory === 'forex' || cleanSymbol.includes('/') || 
      (cleanSymbol.length === 6 && !CRYPTO_ICONS[cleanSymbol.substring(0, 3)] && !cleanSymbol.includes('OIL'))) {
    return <ForexIcon symbol={symbol} size={size} />;
  }
  
  if (normalizedCategory === 'crypto' || getCryptoSymbol(symbol)) {
    return <CryptoIcon symbol={symbol} size={size} isDark={isDark} />;
  }
  
  if (normalizedCategory === 'indices' || INDEX_ICONS[cleanSymbol]) {
    return <IndexIcon symbol={symbol} size={size} isDark={isDark} />;
  }
  
  const isEU = normalizedCategory === 'eu_stocks' || 
               normalizedCategory === 'stocks_eu' || 
               cleanSymbol.endsWith('.DE') || 
               cleanSymbol.endsWith('.PA') || 
               cleanSymbol.endsWith('.SW') || 
               cleanSymbol.endsWith('.AS') || 
               cleanSymbol.endsWith('.L') || 
               cleanSymbol.endsWith('.MC') || 
               cleanSymbol.endsWith('.MI') || 
               ['ASML', 'SAP'].includes(cleanSymbol);

  if (normalizedCategory === 'stocks' || normalizedCategory === 'bist' || normalizedCategory === 'eu_stocks' || normalizedCategory === 'stocks_eu' ||
      cleanSymbol.endsWith('.IS') || STOCK_LOGOS[cleanSymbol.replace('.IS', '')] || isBIST || isEU) {
    return <StockIcon symbol={symbol} size={size} isDark={isDark} isBIST={isBIST} isEU={isEU} />;
  }
  
  // Default fallback
  return (
    <div 
      className={`flex items-center justify-center rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}
      style={{ width: size, height: size }}
    >
      <span className={isDark ? 'text-gray-400' : 'text-gray-500'} style={{ fontSize: size * 0.5 }}>$</span>
    </div>
  );
};

export default memo(InstrumentIcon);
